import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/SEO";
import ToolShell, { Field, inputCls, StatCard } from "../../components/ToolShell";
import { Reveal } from "../../components/Reveal";
import { api } from "../../lib/api";

const listBlock = (title, items, color = "text-foreground") => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
    <h3 className="font-serif text-xl">{title}</h3>
    <ul className="mt-3 space-y-2">
      {items.map((it, i) => (
        <li key={i} className={`flex gap-2 text-sm ${color}`}>
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
          <span className="text-muted-foreground">{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const textBlock = (title, text) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
    <h3 className="font-serif text-xl">{title}</h3>
    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
  </div>
);

export default function NumerologyCalculator() {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !dob) return toast.error("Please enter your name and date of birth.");
    setLoading(true);
    try {
      const { data } = await api.post("/tools/numerology", { full_name: name, dob });
      setResult(data);
      setTimeout(() => document.getElementById("num-result")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolShell slug="numerology-calculator" name="Numerology Calculator" tagline="Your complete number blueprint">
      <SEO title="Numerology Calculator" description="Free numerology calculator — find your Mulank, Bhagyank, name number, lucky number, colour, day and full personality reading." path="/tools/numerology-calculator" />

      <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2 max-w-2xl rounded-3xl border border-border bg-card p-7 shadow-soft" data-testid="numerology-form">
        <Field label="Full Name">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rahul Sharma" data-testid="num-name-input" />
        </Field>
        <Field label="Date of Birth">
          <input type="date" className={inputCls} value={dob} onChange={(e) => setDob(e.target.value)} data-testid="num-dob-input" />
        </Field>
        <div className="sm:col-span-2">
          <button disabled={loading} data-testid="num-submit"
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-gold" />}
            {loading ? "Reading your numbers…" : "Reveal my numbers"}
          </button>
        </div>
      </form>

      {result && (
        <motion.div id="num-result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-14" data-testid="numerology-result">
          <div className="rounded-3xl bg-primary text-primary-foreground p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.2em] text-gold">{result.title} · ruled by {result.planet}</p>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl">{result.full_name}</h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl bg-white/10 p-5 text-center"><p className="text-xs uppercase tracking-[0.15em] text-gold">Mulank</p><p className="mt-1 font-serif text-5xl">{result.mulank}</p></div>
              <div className="rounded-2xl bg-white/10 p-5 text-center"><p className="text-xs uppercase tracking-[0.15em] text-gold">Bhagyank</p><p className="mt-1 font-serif text-5xl">{result.bhagyank}</p></div>
              <div className="rounded-2xl bg-white/10 p-5 text-center"><p className="text-xs uppercase tracking-[0.15em] text-gold">Name No.</p><p className="mt-1 font-serif text-5xl">{result.name_number}</p></div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Lucky Number" value={result.lucky_number} />
            <StatCard label="Lucky Day" value={<span className="text-2xl leading-[3.5rem]">{result.lucky_day}</span>} />
            <StatCard label="Lucky Direction" value={<span className="text-2xl leading-[3.5rem]">{result.lucky_direction}</span>} />
            <StatCard label="Lucky Colour" value={<span className="text-lg leading-[4rem]">{result.lucky_color}</span>} />
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {textBlock("Personality", result.personality)}
            {textBlock("Life Purpose", result.life_purpose)}
            {listBlock("Strengths", result.strengths)}
            {listBlock("Weaknesses", result.weaknesses)}
            {textBlock("Career Guidance", result.career)}
            {textBlock("Relationship Guidance", result.relationship)}
            {textBlock("Health Guidance", result.health)}
            {textBlock("Financial Guidance", result.finance)}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <h3 className="font-serif text-xl">Friendly Numbers</h3>
              <div className="mt-3 flex gap-2">{result.friendly_numbers.map((n) => <span key={n} className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-serif text-lg">{n}</span>)}</div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <h3 className="font-serif text-xl">Enemy Numbers</h3>
              <div className="mt-3 flex gap-2">{result.enemy_numbers.length ? result.enemy_numbers.map((n) => <span key={n} className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-serif text-lg">{n}</span>) : <span className="text-sm text-muted-foreground">None — you get along with most numbers!</span>}</div>
            </div>
            {listBlock("Areas of Improvement", result.improvement)}
            {listBlock("Personal Recommendations", result.recommendations, "text-primary")}
          </div>

          <Reveal>
            <div className="mt-8 rounded-2xl border border-gold/30 bg-gold/10 p-6">
              <p className="text-sm text-foreground"><strong>Destiny (Bhagyank {result.bhagyank} — {result.bhagyank_title}):</strong> {result.bhagyank_summary}</p>
            </div>
          </Reveal>
        </motion.div>
      )}

      <section className="mt-20 max-w-3xl" data-testid="numerology-explainer">
        <h2 className="font-serif text-3xl">How your numbers are calculated</h2>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Numerology turns your birth date and name into single digits. This calculator uses the traditional Indian method and shows your Mulank, Bhagyank and name number.
        </p>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Mulank (birth number)</strong> comes from the day you were born. Add the digits of the day until you get one digit from 1 to 9. Born on the 14th: 1 + 4 = 5, so your Mulank is 5. Born on the 9th: your Mulank is 9.
        </p>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Bhagyank (destiny number)</strong> comes from your full date of birth. Add every digit of the day, month and year, then reduce the total to one digit.
        </p>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Name number</strong> is found by turning each letter of your name into a number and adding them. Different systems use different letter values, so the result can vary by system.
        </p>

        <h2 className="mt-12 font-serif text-3xl">Worked example: 14 August 1995</h2>
        <ul className="mt-4 space-y-2 text-muted-foreground">
          <li>Mulank: 1 + 4 = <strong className="text-foreground">5</strong></li>
          <li>Bhagyank: 1 + 4 + 0 + 8 + 1 + 9 + 9 + 5 = 37, then 3 + 7 = 10, then 1 + 0 = <strong className="text-foreground">1</strong></li>
        </ul>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          So this person has Mulank 5 and Bhagyank 1. In Indian numerology, Mulank is said to describe how you naturally think and react, and Bhagyank your life direction.
        </p>

        <h2 className="mt-12 font-serif text-3xl">Ruling planets by Mulank</h2>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          In Indian numerology each number is linked to a planet: 1 Sun, 2 Moon, 3 Jupiter, 4 Rahu, 5 Mercury, 6 Venus, 7 Ketu, 8 Saturn, 9 Mars.
        </p>

        <h2 className="mt-12 font-serif text-3xl">Frequently asked questions</h2>
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="font-serif text-xl">What is the difference between Mulank and Bhagyank?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Mulank comes from your birth day only. Bhagyank comes from your full date of birth. Read our guide to{" "}
              <Link to="/blog/mulank-vs-bhagyank-what-is-the-difference" className="text-primary underline">Mulank vs Bhagyank</Link> for more.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Is this numerology calculator free?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">Yes. You can find your Mulank, Bhagyank and name number for free.</p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Do I need my full name?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">Use your full name as it is usually written, for example on your documents.</p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Is my data stored?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Check our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> for how your details are handled.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Can numerology predict my future?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              No. Numerology is a tool for reflection and self-understanding, not a guarantee of any outcome.
            </p>
          </div>
        </div>
      </section>
    </ToolShell>
  );
}